import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import KpiCard from '../../components/common/KpiCard';
import Pill from '../../components/common/Pill';
import ConfirmarModal from '../../components/common/ConfirmarModal';
import usePaginacao from '../../hooks/usePaginacao';
import * as auditoriaApi from '../../api/auditoria';
import { formatDataHora } from '../../utils/date';
import { labelPerfil } from '../../utils/webhookOpcoes';

const ACOES_CRITICAS = ['delete', 'purgar', 'rotacionar_credencial', 'login_falha', 'resetar_senha'];

export default function Auditoria() {
  const [logs, setLogs] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtroEntidade, setFiltroEntidade] = useState('');
  const [erro, setErro] = useState('');
  const [confirmandoPurga, setConfirmandoPurga] = useState(false);
  const [resultadoPurga, setResultadoPurga] = useState(null);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const [resLogs, resEstat] = await Promise.all([
        auditoriaApi.listar(filtroEntidade ? { entidade: filtroEntidade, limit: 500 } : { limit: 500 }),
        auditoriaApi.estatisticas(30),
      ]);
      setLogs(resLogs.data);
      setEstatisticas(resEstat.data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar a auditoria.'));
    }
  }, [filtroEntidade]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const { paginados, Paginacao } = usePaginacao(logs || [], 20);

  if (erro && !logs) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!logs) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        titulo="Auditoria"
        subtitulo="Quem fez o quê no portal — o log nunca é apagado"
        acoes={
          <button type="button" className="wh-btn wh-btn-outline" onClick={() => setConfirmandoPurga(true)}>
            <i className="bi bi-eraser" /> Rodar expurgo por retenção
          </button>
        }
      />

      {estatisticas && (
        <div className="row g-3 mb-3">
          <div className="col-6 col-lg-3">
            <KpiCard icone="bi-activity" valor={estatisticas.total_eventos} label="Ações em 30 dias" />
          </div>
          <div className="col-6 col-lg-3">
            <KpiCard icone="bi-box-arrow-in-right" valor={estatisticas.total_logins} label="Logins" />
          </div>
          <div className="col-6 col-lg-3">
            <KpiCard
              icone="bi-shield-exclamation"
              tom={estatisticas.total_login_falha ? 'atencao' : 'neutro'}
              valor={estatisticas.total_login_falha}
              label="Logins recusados"
            />
          </div>
          <div className="col-6 col-lg-3">
            <KpiCard icone="bi-people" valor={estatisticas.por_usuario.length} label="Usuários que entraram" />
          </div>
        </div>
      )}

      {resultadoPurga && (
        <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>
          Expurgo concluído: {resultadoPurga.eventos_removidos} evento(s) removido(s) em{' '}
          {resultadoPurga.endpoints_processados} endpoint(s) com retenção configurada.
        </div>
      )}

      <div className="card-soft">
        <div className="card-soft__header">
          <h2 className="card-soft__title">Registro de ações</h2>
          <select
            className="wh-select"
            style={{ width: 180 }}
            value={filtroEntidade}
            onChange={(e) => setFiltroEntidade(e.target.value)}
          >
            <option value="">Todas as entidades</option>
            <option value="usuario">Usuários</option>
            <option value="endpoint">Endpoints</option>
            <option value="evento">Eventos</option>
          </select>
        </div>
        <div className="wh-table-wrap">
          <table className="wh-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Quem</th>
                <th>Perfil</th>
                <th>Ação</th>
                <th>Descrição</th>
              </tr>
            </thead>
            <tbody>
              {paginados.map((log) => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDataHora(log.criado_em)}</td>
                  <td>{log.email || '—'}</td>
                  <td>{log.perfil ? labelPerfil(log.perfil) : '—'}</td>
                  <td>
                    <Pill tone={ACOES_CRITICAS.includes(log.acao) ? 'danger' : 'neutral'}>{log.acao}</Pill>
                  </td>
                  <td>{log.descricao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Paginacao />

      {confirmandoPurga && (
        <ConfirmarModal
          titulo="Expurgo por retenção"
          mensagem="Cada endpoint tem sua própria retenção em dias (0 = nunca expurgar). Os eventos mais antigos que esse prazo serão apagados definitivamente."
          textoBotao="Rodar expurgo"
          onConfirmar={() => auditoriaApi.purgarEventos().then((r) => setResultadoPurga(r.data)).then(carregar)}
          onClose={() => setConfirmandoPurga(false)}
        />
      )}
    </div>
  );
}
